import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

export type ReviewContext = {
  currentItemId: string;
  expiresAt: string;
  items: Array<{
    id: string;
    resolved: boolean;
    status: string;
    product?: {
      title?: string | null;
      imageUrl?: string | null;
    } | null;
  }>;
  mediaRewardAvailable?: boolean;
  progress: {
    completed: number;
    current: number;
    remaining: number;
    total: number;
  };
  verifiedBuyer: boolean;
  product?: {
    title?: string | null;
    imageUrl?: string | null;
  } | null;
};

type StagedTarget = {
  url: string;
  resourceUrl: string;
  parameters: Array<{ name: string; value: string }>;
};

type SubmitState =
  | { status: "loading"; message: string }
  | { status: "ready"; message: string }
  | { status: "staging"; message: string }
  | { status: "submitting"; message: string }
  | { status: "submitted"; message: string; couponPending: boolean; nextItemId: string | null; remaining: number }
  | { status: "skipped"; message: string; nextItemId: string | null; remaining: number }
  | { status: "error"; message: string };

const ratingLabels: Record<number, string> = {
  1: "Poor",
  2: "Fair",
  3: "Good",
  4: "Great",
  5: "Excellent",
};

export function canSubmitReviewForItem(
  context: Pick<ReviewContext, "currentItemId"> | null,
  targetItemId: string | undefined,
  status: SubmitState["status"],
) {
  const intendedItemId = targetItemId ?? context?.currentItemId;
  return status === "ready" && Boolean(intendedItemId) && context?.currentItemId === intendedItemId;
}

export function ReviewFormPage() {
  const { token } = useParams();
  const [context, setContext] = useState<ReviewContext | null>(null);
  const [rating, setRating] = useState(5);
  const [files, setFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<SubmitState>({ status: "loading", message: "Checking your secure review link" });
  const [targetItemId, setTargetItemId] = useState<string>();

  const productTitle = context?.product?.title ?? "your recent order";
  const currentItemId = targetItemId ?? context?.currentItemId;
  const reviewFormReady = canSubmitReviewForItem(context, targetItemId, status.status);
  const progress = context?.progress;
  const expiresAt = useMemo(() => {
    if (!context?.expiresAt) return "";
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(context.expiresAt));
  }, [context?.expiresAt]);
  const selectedPhotoCount = files.filter((file) => file.type.startsWith("image/")).length;
  const selectedVideoCount = files.filter((file) => file.type.startsWith("video/")).length;
  const selectedMediaLabel = files.length
    ? `${selectedPhotoCount} photo${selectedPhotoCount === 1 ? "" : "s"}, ${selectedVideoCount} video${
        selectedVideoCount === 1 ? "" : "s"
      }`
    : "No media selected";

  useEffect(() => {
    let active = true;

    async function loadContext() {
      if (!token) {
        setStatus({ status: "error", message: "This review link is missing its token." });
        return;
      }

      try {
        const itemQuery = targetItemId ? `?itemId=${encodeURIComponent(targetItemId)}` : "";
        const response = await fetch(`/api/review/${encodeURIComponent(token)}${itemQuery}`, {
          headers: { Accept: "application/json" },
        });
        const payload = (await response.json().catch(() => null)) as { context?: ReviewContext; error?: { message?: string } } | null;
        if (!response.ok) throw new Error(payload?.error?.message ?? "Review link is unavailable");
        if (active) {
          setContext(payload?.context ?? null);
          setStatus({ status: "ready", message: "" });
        }
      } catch (error) {
        if (active) {
          setStatus({
            status: "error",
            message: error instanceof Error ? error.message : "Review link is unavailable",
          });
        }
      }
    }

    void loadContext();
    return () => {
      active = false;
    };
  }, [targetItemId, token]);

  async function uploadToShopify(target: StagedTarget, file: File) {
    const form = new FormData();
    for (const parameter of target.parameters) form.append(parameter.name, parameter.value);
    form.append("file", file);
    const response = await fetch(target.url, { method: "POST", body: form });
    if (!response.ok) throw new Error(`Upload failed for ${file.name}`);
    return {
      sourceUrl: target.resourceUrl,
      mimeType: file.type,
      bytes: file.size,
      alt: `${rating}-star customer review media for ${productTitle}`,
    };
  }

  async function stageMedia() {
    if (!files.length) return [];
    if (!token) throw new Error("Review token is missing.");

    setStatus({ status: "staging", message: "Preparing secure media upload" });
    const stagedResponse = await fetch("/api/review/staged-uploads", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        token,
        itemId: currentItemId,
        files: files.map((file) => ({ filename: file.name, mimeType: file.type, bytes: file.size })),
      }),
    });
    const stagedPayload = (await stagedResponse.json().catch(() => null)) as
      | { targets?: StagedTarget[]; error?: { message?: string } }
      | null;
    if (!stagedResponse.ok) {
      throw new Error(stagedPayload?.error?.message ?? "Media upload could not be prepared.");
    }
    const targets = stagedPayload?.targets ?? [];
    if (targets.length !== files.length) throw new Error("Media upload setup returned an unexpected file count.");

    setStatus({ status: "staging", message: "Uploading media securely" });
    return Promise.all(targets.map((target, index) => uploadToShopify(target, files[index])));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || !reviewFormReady || !currentItemId) return;

    const formData = new FormData(event.currentTarget);
    if (formData.get("consentReview") !== "on") {
      setStatus({ status: "error", message: "Consent is required before the review can be submitted." });
      return;
    }

    if (selectedPhotoCount > 5 || selectedVideoCount > 2) {
      setStatus({ status: "error", message: "Attach up to 5 photos and 2 videos." });
      return;
    }

    try {
      const uploadedMedia = await stageMedia();
      setStatus({ status: "submitting", message: "Saving your review for moderation" });

      const response = await fetch("/api/review/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          token,
          itemId: currentItemId,
          rating,
          title: String(formData.get("title") ?? "").trim(),
          body: String(formData.get("body") ?? "").trim(),
          displayName: String(formData.get("displayName") ?? "").trim(),
          nps: formData.get("nps") ? Number(formData.get("nps")) : undefined,
          consent: {
            review: true,
            media: uploadedMedia.length > 0 || formData.get("consentMedia") === "on",
            social: formData.get("consentSocial") === "on",
          },
          media: uploadedMedia,
        }),
      });
      const payload = (await response.json().catch(() => null)) as
        | { completed?: boolean; error?: { message?: string }; nextItemId?: string | null; remaining?: number }
        | null;
      if (!response.ok) throw new Error(payload?.error?.message ?? "Review could not be submitted.");
      const remaining = payload?.remaining ?? Math.max(0, (context?.progress.remaining ?? 1) - 1);
      setStatus({
        status: "submitted",
        message: payload?.completed
          ? "Your review is saved and waiting for store approval."
          : "Your review is saved. Use the next product link in your order message to continue reviewing your items.",
        couponPending: uploadedMedia.length > 0 && context?.mediaRewardAvailable === true,
        nextItemId: payload?.nextItemId ?? null,
        remaining,
      });
      setFiles([]);
    } catch (error) {
      setStatus({
        status: "error",
        message: error instanceof Error ? error.message : "Review could not be submitted.",
      });
    }
  }

  async function skipItem() {
    if (!token || !reviewFormReady || !currentItemId) return;
    try {
      setStatus({ status: "submitting", message: "Skipping this item" });
      const response = await fetch("/api/review/skip", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ token, itemId: currentItemId }),
      });
      const payload = (await response.json().catch(() => null)) as
        | { completed?: boolean; error?: { message?: string }; nextItemId?: string | null; remaining?: number }
        | null;
      if (!response.ok) throw new Error(payload?.error?.message ?? "This item could not be skipped.");
      setStatus({
        status: "skipped",
        message: payload?.completed
          ? "This review request is complete. Thank you for your time."
          : "This item is skipped. Continue to review the next item from this secure form.",
        nextItemId: payload?.nextItemId ?? null,
        remaining: payload?.remaining ?? Math.max(0, (context?.progress.remaining ?? 1) - 1),
      });
    } catch (error) {
      setStatus({
        status: "error",
        message: error instanceof Error ? error.message : "This item could not be skipped.",
      });
    }
  }

  function continueToItem(itemId: string) {
    setFiles([]);
    setRating(5);
    setStatus({ status: "loading", message: "Loading the next item" });
    setTargetItemId(itemId);
  }

  if (status.status === "loading") {
    return (
      <main className="wr-review-form-page">
        <section className="wr-review-form-card" role="status">
          <div className="wr-form-skeleton" />
          <p>{status.message}</p>
        </section>
      </main>
    );
  }

  if (status.status === "submitted" || status.status === "skipped") {
    return (
      <main className="wr-review-form-page">
        <section className="wr-review-form-card wr-review-form-card--center" aria-labelledby="wr-thanks-title">
          <p className="wr-review-kicker">{status.status === "submitted" ? "Review received" : "Item skipped"}</p>
          <h1 id="wr-thanks-title">{status.status === "submitted" ? "Thank you for helping future customers." : "Thanks for letting us know."}</h1>
          <p>{status.message}</p>
          {status.remaining > 0 ? (
            <div className="wr-next-box">
              <strong>{status.remaining} item{status.remaining === 1 ? "" : "s"} remaining.</strong>
              {status.nextItemId ? (
                <button className="wr-button" type="button" onClick={() => continueToItem(status.nextItemId as string)}>
                  Continue to next item
                </button>
              ) : (
                <p>Choose another unresolved item from this secure review request.</p>
              )}
            </div>
          ) : null}
          {status.status === "submitted" && status.couponPending ? (
            <div className="wr-coupon-box" aria-label="Media reward status">
              <span>Media reward pending</span>
              <strong>After approval</strong>
              <p>The store will issue the configured coupon after moderation.</p>
            </div>
          ) : status.status === "submitted" ? (
            <div className="wr-next-box">
              <strong>Approval is next.</strong>
              <p>The store will publish your review after moderation.</p>
            </div>
          ) : null}
        </section>
      </main>
    );
  }

  return (
    <main className="wr-review-form-page">
      <section className="wr-review-form-card">
        <header className="wr-product-strip">
          <div className="wr-product-thumb" aria-hidden="true">
            {context?.product?.imageUrl ? <img src={context.product.imageUrl} alt="" /> : <span>W</span>}
          </div>
          <div>
            <p className="wr-review-kicker">{context?.verifiedBuyer ? "Verified order" : "Review request"}</p>
            <h1 id="wr-review-title">How was {productTitle}?</h1>
            <p>Your review helps other customers choose with confidence.</p>
            {progress ? <small>Review {progress.current} of {progress.total} · {progress.completed} completed · {progress.remaining} remaining</small> : null}
            {expiresAt ? <small>This secure link expires {expiresAt}.</small> : null}
          </div>
        </header>

        {context?.items.length && currentItemId ? (
          <nav aria-label="Items in this review request">
            <ul className="wr-review-request-items">
              {context.items.map((item, index) => (
                <li key={item.id}>
                  <button
                    type="button"
                    aria-current={item.id === currentItemId ? "step" : undefined}
                    disabled={item.resolved || item.id === currentItemId}
                    onClick={() => continueToItem(item.id)}
                  >
                    {item.product?.title ?? `Item ${index + 1}`}
                    {item.resolved ? " · completed" : item.id === currentItemId ? " · current" : " · review"}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}

        {status.status === "error" && !context ? (
          <div className="wr-token-state" role="alert">
            <strong>{status.message}</strong>
            <p>Contact the store if you need a fresh review request.</p>
          </div>
        ) : (
          <form key={currentItemId} className="wr-customer-form" encType="multipart/form-data" onSubmit={handleSubmit}>
            <fieldset className="wr-rating-field">
              <legend>Choose a rating</legend>
              <div className="wr-star-input" role="radiogroup" aria-label={`Rating for ${productTitle}`}>
                {[1, 2, 3, 4, 5].map((score) => (
                  <label key={score} className="wr-star-choice">
                    <input
                      type="radio"
                      name="rating"
                      value={score}
                      checked={score === rating}
                      onChange={() => setRating(score)}
                    />
                    <span aria-hidden="true">★</span>
                    <small>{ratingLabels[score]}</small>
                  </label>
                ))}
              </div>
            </fieldset>

            <label>
              Review title (optional)
              <input name="title" maxLength={120} placeholder="What should shoppers know first?" autoComplete="off" />
            </label>

            <button className="wr-button wr-button--ghost wr-button--full" type="button" onClick={skipItem} disabled={!reviewFormReady}>
              Skip this item
            </button>

            <label>
              Review body
              <textarea
                name="body"
                rows={6}
                minLength={3}
                maxLength={5000}
                required
                placeholder="Share the details that made the product worth buying."
              />
            </label>

            <div className="wr-media-picker">
              <label>
                Photo or video
                <input
                  name="media"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
                  multiple
                  onChange={(event) => setFiles(Array.from(event.currentTarget.files ?? []).slice(0, 7))}
                />
              </label>
              <p>{selectedMediaLabel}. Limit 5 photos and 2 videos.</p>
              {files.length ? (
                <ul className="wr-media-list" aria-label="Selected media files">
                  {files.map((file) => (
                    <li key={`${file.name}-${file.size}`}>
                      <span>{file.name}</span>
                      <small>{formatBytes(file.size)}</small>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <label>
              Display name
              <input name="displayName" maxLength={80} required placeholder="Example: Mariam A." autoComplete="name" />
            </label>

            <label>
              Store experience score
              <select name="nps" defaultValue="">
                <option value="">Optional</option>
                {Array.from({ length: 11 }, (_, value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <div className="wr-consent-stack">
              <label className="wr-checkbox">
                <input type="checkbox" name="consentReview" required />
                <span>I agree that the store may display my review.</span>
              </label>
              <label className="wr-checkbox">
                <input type="checkbox" name="consentMedia" />
                <span>I agree that uploaded media may appear with my review.</span>
              </label>
              <label className="wr-checkbox">
                <input type="checkbox" name="consentSocial" />
                <span>The store may ask to reuse this review in social content.</span>
              </label>
            </div>

            {status.status === "error" ? (
              <p className="wr-form-alert" role="alert">
                {status.message}
              </p>
            ) : status.status === "staging" || status.status === "submitting" ? (
              <p className="wr-form-alert wr-form-alert--working" aria-live="polite">
                {status.message}
              </p>
            ) : null}

            <button
              className="wr-button wr-button--full"
              type="submit"
              disabled={!reviewFormReady || status.status === "staging" || status.status === "submitting"}
            >
              {status.status === "staging" || status.status === "submitting" ? "Submitting" : "Submit review"}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
